/**
 * Lines, counted rather than measured.
 *
 * The JSON editor sizes itself with `rows` and paints a line-number gutter, and
 * both are exact — not approximations — because a code editor does **not**
 * soft-wrap (`wrap="off"`), so one logical line is exactly one line box. That is
 * what lets the whole thing work with no `scrollHeight`, no `ResizeObserver` and
 * no measurement at all: nothing to drift, and true in jsdom, where every
 * measurement is `0` forever.
 *
 * `\r\n` and a lone `\r` each count as ONE break. A textarea normalises to `\n`,
 * but pasted text arrives however the operator's other application wrote it, and
 * counting `\r\n` as two would draw a phantom gutter number per line on every
 * paste from Windows.
 */

/** Matches the three line terminators JSON text can arrive with. */
const LINE_BREAK = /\r\n|\r|\n/gu;

export interface LineColumn {
  /** 1-based, as an operator counts. */
  line: number;
  /** 1-based, in characters. */
  column: number;
}

/** The offset of the first character of each line. Always at least `[0]`. */
export function lineStartOffsets(text: string): number[] {
  const starts = [0];
  const pattern = new RegExp(LINE_BREAK.source, 'gu');
  let match = pattern.exec(text);

  while (match !== null) {
    starts.push(match.index + match[0].length);
    match = pattern.exec(text);
  }

  return starts;
}

/**
 * How many lines the text occupies — one more than its break count, so an empty
 * document is one line (the caret is already standing on it) and a trailing
 * break opens a real, numbered, empty last line.
 */
export function countLines(text: string): number {
  return lineStartOffsets(text).length;
}

/**
 * Turns a parse offset into something to say out loud.
 *
 * Out-of-range offsets clamp rather than throw: a caller pointing past the end
 * is showing an operator where their document ran out, and the last position is
 * the true answer to that.
 */
export function offsetToLineColumn(text: string, offset: number): LineColumn {
  const clamped = Math.max(0, Math.min(offset, text.length));
  const starts = lineStartOffsets(text);

  let line = 1;
  for (let index = 0; index < starts.length; index += 1) {
    if ((starts[index] ?? 0) <= clamped) line = index + 1;
    else break;
  }

  return { line, column: clamped - (starts[line - 1] ?? 0) + 1 };
}
